/**
 * Spec 218 — ouderschaps-/geboorteverlof direct boeken namens een medewerker
 * (backoffice `/verlof/ouderschap-boeken`). Gedeeld request-schema + preview-DTO:
 * de API valideert ermee én het formulier valideert er client-side mee, zodat
 * het form niets kan sturen dat de API weigert.
 *
 * Drie manieren om dagen te kiezen (precies één):
 *  - periode: `vanDatum` (+ optioneel `totDatum`) — een losse dag of een
 *    aaneengesloten periode (weekend/feestdag/0-urendag overgeslagen);
 *  - losse dagen: `datums` — exact die dagen;
 *  - weekpatroon: `patroon` — vaste weekdag(en) binnen een periode, elke week
 *    of om de N weken ("elke woensdag", "om de week maandag + vrijdag").
 * Elke gekozen dag wordt een eigen DEFINITIEVE opname (los in te trekken).
 */
import { z } from 'zod'

export const OUDERSCHAP_BOEKBARE_TYPES = [
  'OUDERSCHAP_BETAALD',
  'OUDERSCHAP_ONBETAALD',
  'GEBOORTE',
  'GEBOORTE_AANVULLEND',
  'ZWANGERSCHAP',
  'ONBETAALD',
] as const
export type OuderschapBoekType = (typeof OUDERSCHAP_BOEKBARE_TYPES)[number]

export const OUDERSCHAP_BOEK_TYPE_LABELS: Record<OuderschapBoekType, string> = {
  OUDERSCHAP_BETAALD: 'Ouderschapsverlof - betaald deel',
  OUDERSCHAP_ONBETAALD: 'Ouderschapsverlof - onbetaald deel',
  GEBOORTE: 'Geboorteverlof (100%)',
  GEBOORTE_AANVULLEND: 'Aanvullend geboorteverlof (70%)',
  ZWANGERSCHAP: 'Zwangerschaps-/bevallingsverlof',
  ONBETAALD: 'Onbetaald verlof',
}

/** Typen die op een kind-emmer boeken (kind verplicht). */
export function isOuderschapBoekType(t: OuderschapBoekType): boolean {
  return t === 'OUDERSCHAP_BETAALD' || t === 'OUDERSCHAP_ONBETAALD'
}

/** Maximaal aantal dagen per boeking (één jaar). */
export const OUDERSCHAP_BOEK_MAX_DAGEN = 366

// ── batch-71 ouderschap-verlof (FO-540): aanvullen met vakantie-uren ─────────

/**
 * De verloftypen met een UWV-uitkering van 70%, waarop een medewerker de rest mag aanvullen.
 * Geboorteverlof (100%) heeft nooit aanvulling; de API weigert hem daar ook.
 */
export const AANVULBARE_VERLOF_TYPES = ['GEBOORTE_AANVULLEND', 'OUDERSCHAP_BETAALD'] as const

export function isAanvulbaarVerlofType(type: string): boolean {
  return (AANVULBARE_VERLOF_TYPES as readonly string[]).includes(type)
}

/** De melding bij een verkeerd type: één tekst voor het form en de API. */
export const AANVULLING_NIET_TOEGESTAAN =
  'Aanvullen met vakantie-uren kan alleen bij aanvullend geboorteverlof en betaald ouderschapsverlof.'

/** De emmers waaruit de aanvulling komt: alleen vakantie-uren, geen ATV of TVT (PO-beslispunt 3). */
export const AANVULLING_EMMER_TYPES = ['WETTELIJK', 'BOVENWETTELIJK'] as const
export type AanvullingEmmerType = (typeof AANVULLING_EMMER_TYPES)[number]

export const AANVULLING_EMMER_LABELS: Record<AanvullingEmmerType, string> = {
  WETTELIJK: 'Wettelijk verlof',
  BOVENWETTELIJK: 'Bovenwettelijk verlof',
}

/** `bronReferentie` van de mutaties van een aanvulling; telt niet als opgenomen verlof in de verloning. */
export const AANVULLING_REF_PREFIX = 'ouderschap-aanvulling:'

/**
 * De HR-keuze bij te weinig vakantiesaldo (FO-522: nooit stil negatief): negatief boeken op
 * een gekozen emmer, minder aanvullen (alleen wat het saldo dekt) of niet aanvullen.
 */
export const AANVULLING_TEKORT_KEUZES = ['NEGATIEF_OP_EMMER', 'MINDER_AANVULLEN', 'NIET_AANVULLEN'] as const
export type AanvullingTekortKeuze = (typeof AANVULLING_TEKORT_KEUZES)[number]

export const AANVULLING_TEKORT_KEUZE_LABELS: Record<AanvullingTekortKeuze, string> = {
  NEGATIEF_OP_EMMER: 'Toestaan: negatief boeken op een gekozen emmer',
  MINDER_AANVULLEN: 'Minder aanvullen: alleen wat het saldo dekt',
  NIET_AANVULLEN: 'Niet aanvullen',
}

export const AanvullingTekortAfhandelingSchema = z
  .object({
    keuze: z.enum(AANVULLING_TEKORT_KEUZES),
    emmerType: z.enum(AANVULLING_EMMER_TYPES).optional(),
    reden: z.string().trim().min(10, 'Geef een reden van minstens 10 tekens').max(1000, 'Maximaal 1000 tekens'),
  })
  .superRefine((v, ctx) => {
    if (v.keuze === 'NEGATIEF_OP_EMMER' && !v.emmerType) {
      ctx.addIssue({ code: 'custom', path: ['emmerType'], message: 'Kies de emmer die negatief mag worden' })
    }
  })
export type AanvullingTekortAfhandeling = z.infer<typeof AanvullingTekortAfhandelingSchema>

/** Foutcode (409) wanneer het vakantiesaldo de aanvulling niet dekt en er geen besluit is. */
export const AANVULLING_TEKORT_CODE = 'AANVULLING_TEKORT' as const

/** `details` bij een 409 AANVULLING_TEKORT. */
export interface AanvullingTekortDetailsDTO {
  teAanvullenUren: number
  gedektUren: number
  tekortUren: number
  beschikbaarPerType: Array<{ emmerType: AanvullingEmmerType; beschikbaar: number }>
}

/** PATCH `/leave/v2/requests/:id/aanvulling`: aan- of uitzetten op een bestaande opname. */
export const AanvullingWijzigSchema = z.object({
  aanvullen: z.boolean(),
  tekortAfhandeling: AanvullingTekortAfhandelingSchema.optional(),
})
export type AanvullingWijzigRequest = z.infer<typeof AanvullingWijzigSchema>

export interface AanvullingWijzigResultaatDTO {
  aanvullingUren: number | null
  aanvullingPct: number | null
  waarschuwingen: string[]
}

const datum = z.string().date('Ongeldige datum (jjjj-mm-dd)')

export const OuderschapBoekPatroonSchema = z
  .object({
    /** ISO-weekdagen: 1 = maandag … 7 = zondag. */
    weekdagen: z.array(z.number().int().min(1).max(7)).min(1, 'Kies minimaal één weekdag').max(7),
    vanDatum: datum,
    totDatum: datum,
    /** 1 = elke week, 2 = om de week, … (gerekend vanaf de week van `vanDatum`). */
    elkeNWeken: z.number().int().min(1).max(4).default(1),
  })
  .refine((p) => p.totDatum >= p.vanDatum, {
    message: 'De einddatum ligt vóór de begindatum',
    path: ['totDatum'],
  })

export const OuderschapBoekRequestSchema = z
  .object({
    employeeId: z.string().min(1, 'Kies een medewerker'),
    type: z.enum(OUDERSCHAP_BOEKBARE_TYPES),
    childId: z.string().min(1).optional(),
    vanDatum: datum.optional(),
    totDatum: datum.optional(),
    datums: z
      .array(datum)
      .min(1, 'Kies minimaal één dag')
      .max(OUDERSCHAP_BOEK_MAX_DAGEN, `Maximaal ${OUDERSCHAP_BOEK_MAX_DAGEN} dagen per boeking`)
      .optional(),
    patroon: OuderschapBoekPatroonSchema.optional(),
    /** Uren per dag; leeg = de roosteruren van die dag. */
    urenPerDag: z.number().positive().max(24).optional(),
    opmerking: z.string().max(2000).optional(),
    /** batch-71 (FO-540 §3.1): de rest aanvullen met vakantie-uren. Standaard uit. */
    aanvullenMetVakantie: z.boolean().optional(),
    /** Het besluit als het vakantiesaldo de aanvulling niet dekt; geldt voor de hele boeking. */
    aanvullingTekort: AanvullingTekortAfhandelingSchema.optional(),
  })
  .refine(
    (d) =>
      [d.vanDatum !== undefined, d.datums !== undefined, d.patroon !== undefined].filter(Boolean)
        .length === 1,
    {
      message: 'Kies één manier: een periode, losse dagen of een weekpatroon',
      path: ['vanDatum'],
    },
  )
  .refine((d) => d.totDatum === undefined || d.vanDatum !== undefined, {
    message: 'Een einddatum hoort bij een periode met begindatum',
    path: ['totDatum'],
  })
  .refine((d) => !d.vanDatum || !d.totDatum || d.totDatum >= d.vanDatum, {
    message: 'De einddatum ligt vóór de begindatum',
    path: ['totDatum'],
  })
  .refine((d) => !isOuderschapBoekType(d.type) || !!d.childId, {
    message: 'Kies een kind voor ouderschapsverlof',
    path: ['childId'],
  })
  .refine((d) => !d.aanvullenMetVakantie || isAanvulbaarVerlofType(d.type), {
    message: AANVULLING_NIET_TOEGESTAAN,
    path: ['aanvullenMetVakantie'],
  })

export type OuderschapBoekRequest = z.input<typeof OuderschapBoekRequestSchema>

/** Wat er met een kandidaat-dag gebeurt. Alleen BOEKEN telt mee in de totalen. */
export type OuderschapBoekDagStatus =
  | 'BOEKEN'
  | 'AL_GEBOEKT'
  | 'WEEKEND'
  | 'FEESTDAG'
  | 'GEEN_ROOSTERUREN'
  // batch-71 (FO-539): de dag ligt na de vervaldag van het gekozen deel.
  | 'NA_TERMIJN'

export const OUDERSCHAP_BOEK_DAG_STATUS_LABELS: Record<OuderschapBoekDagStatus, string> = {
  BOEKEN: 'Wordt geboekt',
  AL_GEBOEKT: 'Al geboekt',
  WEEKEND: 'Weekend',
  FEESTDAG: 'Feestdag',
  GEEN_ROOSTERUREN: 'Geen roosteruren',
  NA_TERMIJN: 'Na de opnametermijn',
}

export interface OuderschapBoekDagDTO {
  /** yyyy-mm-dd */
  datum: string
  /** ISO-weekdag 1..7 */
  weekdag: number
  status: OuderschapBoekDagStatus
  /** Te boeken uren (0 als de dag niet geboekt wordt). */
  uren: number
  /** Roosteruren van die dag (ter vergelijking met een handmatige urenPerDag). */
  roosterUren: number
  /** Resterend saldo in de kind-emmer ná deze dag (null zonder emmer). */
  saldoNa: number | null
  /** Ander actief verlof op dezelfde dag (informatief, blokkeert niet). */
  anderVerlof: string | null
  /** batch-71 (FO-540 §3.1): de aanvulling op deze dag (0 zonder aanvulling). */
  aanvullingUren: number
  /** Het lopende vakantiesaldo ná deze dag (null zonder aanvulling). */
  vakantieSaldoNa: number | null
}

export interface OuderschapBoekPreviewDTO {
  ok: boolean
  aantalDagen: number
  totaalUren: number
  overgeslagen: number
  medewerker: string | null
  emmer: {
    toegekend: number
    resterendVoor: number
    resterendNa: number
    vervaltOp: string | null
    /** batch-71 (FO-539): de stand van de emmer vóór deze boeking. */
    beschikbaar: number
    verlopen: boolean
    vervallenUren: number
    status: OuderschapEmmerStatus
  } | null
  waarschuwingen: string[]
  fouten: string[]
  dagen: OuderschapBoekDagDTO[]
  /** batch-71 (FO-540 §3.1): de aanvulling van deze boeking; null als hij niet aan staat. */
  aanvulling: {
    /** Het UWV-deel (0.70) dat op de opnames wordt vastgelegd. */
    pct: number
    totaalUren: number
    vakantieSaldoVoor: number
    vakantieSaldoNa: number
    /** Niet door vakantiesaldo gedekt: dan moet HR kiezen (`aanvullingTekort`). */
    tekortUren: number
    beschikbaarPerType: AanvullingTekortDetailsDTO['beschikbaarPerType']
  } | null
}

// ── batch-71 ouderschap-verlof (FO-539): de stand van een kind-emmer ──────────

/**
 * De stand van een ouderschaps-emmer op een peildatum. Eén berekening voor alle
 * schermen (`berekenOuderschapEmmerStand` in `@streep/utils`): een verlopen emmer
 * toont nooit een restant, ook niet als de verval-cron nog niet heeft gedraaid.
 */
export const OUDERSCHAP_EMMER_STATUSSEN = ['GELDIG', 'VERLOOPT_BINNENKORT', 'VERLOPEN'] as const
export type OuderschapEmmerStatus = (typeof OUDERSCHAP_EMMER_STATUSSEN)[number]

/**
 * Vanaf hoeveel dagen voor de vervaldag een emmer met nog beschikbare uren als
 * "verloopt binnenkort" telt (spec 239 A3). De berekening neemt een afwijkende
 * waarde als parameter; er is bewust geen beleidsveld (spec 239 §9: geen schemawijziging).
 */
export const OUDERSCHAP_VERLOOPT_BINNENKORT_DAGEN = 90

export const OUDERSCHAP_EMMER_STATUS_LABELS: Record<OuderschapEmmerStatus, string> = {
  GELDIG: 'Geldig',
  VERLOOPT_BINNENKORT: 'Verloopt binnenkort',
  VERLOPEN: 'Verlopen',
}

/** De velden die elk emmer-DTO uit de gedeelde berekening krijgt. */
export interface OuderschapEmmerStandVelden {
  /** Nog op te nemen uren; 0 zodra de opnametermijn verstreken is, nooit negatief. */
  beschikbaar: number
  /** @deprecated Alias van `beschikbaar` voor bestaande clients. Lees `beschikbaar`. */
  resterend: number
  verlopen: boolean
  /** Uren die door het verstrijken van de termijn zijn vervallen (0 zolang de emmer loopt). */
  vervallenUren: number
  status: OuderschapEmmerStatus
}

/** Kind + ouderschaps-emmers zoals de verlof-schermen ze nodig hebben (AVG-minimaal: geen geboortedatum/notities). */
export interface OuderschapKindEmmerDTO extends OuderschapEmmerStandVelden {
  entitlementId: string
  toegekend: number
  opgenomen: number
  /** yyyy-mm-dd */
  expiresAt: string | null
}

/**
 * Een kind-emmer op `GET /employees/:id/children` en `GET /me/children`
 * (Gezin-tab, dossierkaart, portal-saldokaart). `expiresAt` is daar een volledige
 * ISO-datum; op de verlof-schermen (`OuderschapKindEmmerDTO`) yyyy-mm-dd.
 */
export interface KindEmmerDTO extends OuderschapEmmerStandVelden {
  entitlementId: string
  toegekend: number
  opgenomen: number
  expiresAt: string
}

/** Hoe een scherm een emmer toont: één bron voor de copy van alle vijf de weergaven. */
export interface OuderschapEmmerWeergave {
  /** De hoofdregel: "342 uur resterend" of "Opnametermijn verstreken op ... · ... vervallen". */
  tekst: string
  /** Een aanvullende regel over de termijn; bij VERLOPEN staat die al in `tekst`. */
  termijn: string | null
  /** GRIJS = verlopen, geen actie meer; ORANJE = verloopt binnenkort. */
  toon: 'NORMAAL' | 'ORANJE' | 'GRIJS'
}

/**
 * De tekst bij een emmer. De formatters komen van het scherm: de portal kent
 * uren of dagen, de backoffice alleen uren. Zonder vervaldatum geen termijnregel.
 *
 * "t/m" en niet het "vóór" uit spec 239 §B4: de vervaldag zelf is nog opneembaar
 * (`betaaldDeelNogOpneembaar`), en de andere schermen schrijven ook "t/m".
 */
export function ouderschapEmmerWeergave(
  emmer: Pick<OuderschapEmmerStandVelden, 'beschikbaar' | 'vervallenUren' | 'status'> & {
    expiresAt: string | null
  },
  fmt: { uren: (uren: number) => string; datum: (iso: string) => string },
): OuderschapEmmerWeergave {
  const datum = emmer.expiresAt ? fmt.datum(emmer.expiresAt) : null
  if (emmer.status === 'VERLOPEN') {
    const vervallen = `${fmt.uren(emmer.vervallenUren)} vervallen`
    return {
      tekst: datum ? `Opnametermijn verstreken op ${datum} · ${vervallen}` : `Opnametermijn verstreken · ${vervallen}`,
      termijn: null,
      toon: 'GRIJS',
    }
  }
  if (emmer.status === 'VERLOOPT_BINNENKORT') {
    return {
      tekst: datum
        ? `nog ${fmt.uren(emmer.beschikbaar)} · opnemen t/m ${datum}`
        : `nog ${fmt.uren(emmer.beschikbaar)}`,
      termijn: null,
      toon: 'ORANJE',
    }
  }
  return {
    tekst: `${fmt.uren(emmer.beschikbaar)} resterend`,
    termijn: datum ? `Opnemen t/m ${datum}` : null,
    toon: 'NORMAAL',
  }
}

export interface OuderschapKindDTO {
  id: string
  naam: string
  betaald: OuderschapKindEmmerDTO | null
  onbetaald: OuderschapKindEmmerDTO | null
}

// ── batch-71 ouderschap-verlof (FO-539 §C/§D): HR-lijst en correctierun ──────

/** De `bronReferentie` van de VERVAL-mutaties en tegenboekingen van een correctierun. */
export const OUDERSCHAP_VERVAL_CORRECTIE_PREFIX = 'ouderschap-verval-correctie:'
export const OUDERSCHAP_VERVAL_ROLLBACK_PREFIX = 'ouderschap-verval-correctie-rollback:'
/** `nieuweWaarde.bron` op de `VerlofAudit` van een correctierun (leesbron van de signalen). */
export const OUDERSCHAP_VERVAL_AUDIT_BRON = 'OUDERSCHAP_VERVAL_CORRECTIE'
export const OUDERSCHAP_VERVAL_ROLLBACK_AUDIT_BRON = 'OUDERSCHAP_VERVAL_ROLLBACK'
/** Een medewerker krijgt alleen een melding als de termijn korter dan dit geleden verstreek (A2). */
export const OUDERSCHAP_VERVAL_MELDING_MAANDEN = 12

/** Waarom een emmer op de HR-lijst staat. */
export type OuderschapVervalHrReden =
  | 'BESCHERMD'
  | 'NIET_DEFINITIEF'
  | 'INCONSISTENT'
  | 'TE_VERVALLEN'
  | 'OPGENOMEN_NA_TERMIJN'
  | 'BOVEN_RECHT'

export const OUDERSCHAP_VERVAL_HR_REDEN_LABELS: Record<OuderschapVervalHrReden, string> = {
  BESCHERMD: 'Beschermd: vervalt niet vanzelf',
  NIET_DEFINITIEF: 'Niet definitief: vervalt niet vanzelf',
  INCONSISTENT: 'Inconsistent: staat op vervallen, maar de termijn loopt nog',
  TE_VERVALLEN: 'Wacht op verval: de verval-cron of de correctierun pakt hem op',
  OPGENOMEN_NA_TERMIJN: 'Geboekt na de vervaldag',
  BOVEN_RECHT: 'Meer opgenomen dan het recht',
}

/**
 * De redenen die om een besluit van HR vragen; alleen die leveren een melding op. De andere
 * zijn informatie (review I6): die staan op de lijst, binnen het meldvenster van
 * `OUDERSCHAP_VERVAL_MELDING_MAANDEN`, maar geven geen melding.
 */
export const OUDERSCHAP_VERVAL_HR_BESLUIT_REDENEN: readonly OuderschapVervalHrReden[] = [
  'BESCHERMD',
  'NIET_DEFINITIEF',
  'INCONSISTENT',
]

/**
 * Eén regel van de HR-lijst `/verlof/ouderschap-verval`. Bewust zonder kindnaam of
 * geboortedatum (AVG): HR opent de Gezin-tab van de medewerker als dat nodig is.
 */
export interface OuderschapVervalHrRegelDTO {
  entitlementId: string
  employeeId: string
  medewerker: string
  /** OUDERSCHAP_BETAALD of OUDERSCHAP_ONBETAALD */
  type: string
  /** yyyy-mm-dd */
  vervaltOp: string
  redenen: OuderschapVervalHrReden[]
  /** Uit `vervalBeschermdReden`, als die er is. */
  toelichting: string | null
  /** Wat de emmer nog zou tonen zonder de vervaldag: het restant dat vervalt of blijft hangen. */
  restantUren: number
  /** Netto opname met een boekdatum na de vervaldag. */
  opgenomenNaTermijnUren: number
  /** Meer opgenomen dan het recht (alleen bij een verlopen emmer op de lijst). */
  bovenRechtUren: number
}

export interface OuderschapVervalHrLijstDTO {
  regels: OuderschapVervalHrRegelDTO[]
  totalen: Record<OuderschapVervalHrReden, number>
}
